import React, { useState, useEffect } from 'react'
import { useI18n } from '../../i18n'
import { API_ENDPOINTS } from '../../config/api'
import { Button } from '../ui/Button'
import { Select } from '../ui/Select'
import { GovernanceInlineFormPanel } from './GovernanceInlineFormPanel'
import listStyles from '@/styles/pages/governance/GovernanceListing.module.scss'

interface Template {
  id: string
  code: string
  name: string
  description?: string
}

interface CreateFromTemplateModalProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
}

const CreateFromTemplateModal: React.FC<CreateFromTemplateModalProps> = ({ open, onClose, onSuccess }) => {
  const { t } = useI18n()
  const [loading, setLoading] = useState(false)
  const [templates, setTemplates] = useState<Template[]>([])
  const [loadingTemplates, setLoadingTemplates] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState('')
  const [formData, setFormData] = useState({
    companyName: '',
    owner: '',
    department: '',
    effectiveFrom: ''
  })

  useEffect(() => {
    if (open) {
      loadTemplates()
    }
  }, [open])

  const loadTemplates = async () => {
    try {
      setLoadingTemplates(true)
      const token = localStorage.getItem('token') || ''
      const res = await fetch(API_ENDPOINTS.GOVERNANCE_POLICY_TEMPLATES, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (res.ok) {
        const data = await res.json()
        setTemplates(data.templates || [])
      }
    } catch (error) {
      console.error('[CreateFromTemplateModal] Error loading templates:', error)
    } finally {
      setLoadingTemplates(false)
    }
  }

  if (!open) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedTemplate) return

    try {
      setLoading(true)
      const token = localStorage.getItem('token') || ''
      const res = await fetch(API_ENDPOINTS.GOVERNANCE_POLICY_GENERATE_DOC(selectedTemplate), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      })

      const data = await res.json()
      
      if (res.status === 501) {
        // Feature not yet implemented
        alert(t('governance.policies.template.notImplemented', 'Document generation is not yet implemented. This feature will be available in a future update.'))
      } else if (!res.ok) {
        throw new Error(data.error || 'Failed to generate document')
      }

      onSuccess()
      onClose()
    } catch (error: any) {
      console.error('[CreateFromTemplateModal] Error:', error)
      alert(error.message || t('governance.policies.template.error', 'Failed to generate document'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <GovernanceInlineFormPanel
      title={t('governance.policies.template.title', 'Create from Template')}
      onClose={onClose}
      footer={
        <>
          <Button type="button" color="outline" size="small" onClick={onClose} disabled={loading}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            type="submit"
            form="governance-policy-template-form"
            color="primary"
            size="small"
            disabled={loading || !selectedTemplate}
          >
            {loading ? t('common.generating', 'Generating…') : t('governance.policies.template.generate', 'Generate Document')}
          </Button>
        </>
      }
    >
      <form id="governance-policy-template-form" onSubmit={handleSubmit}>
          <div>
            <label className="block text-sm font-medium mb-1">
              {t('governance.policies.template.selectTemplate', 'Select Template')} <span className="text-red-500">*</span>
            </label>
            {loadingTemplates ? (
              <div className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md">
                {t('common.loading', 'Loading...')}
              </div>
            ) : (
              <Select<string>
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:border-gray-600 dark:bg-neutral-800"
                placeholder={t('governance.policies.template.selectPlaceholder', '-- Select a template --')}
                options={templates.map((template) => ({
                  value: template.code,
                  label: `${template.name} (${template.code})`,
                }))}
                value={selectedTemplate || null}
                onChange={(v) => setSelectedTemplate(v ?? '')}
              />
            )}
            {templates.length === 0 && !loadingTemplates && (
              <div className="text-sm text-gray-500 mt-1 space-y-2">
                <p className="font-medium text-orange-600">
                  {t('governance.policies.template.noTemplates', 'No templates available.')}
                </p>
                <p className="text-xs">
                  {t('governance.policies.template.adminInfo', 'Administrators can upload templates via API: POST /api/governance/policy-templates with form-data (file, code, name, description)')}
                </p>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                {t('governance.policies.template.companyName', 'Company Name')}
              </label>
              <input
                type="text"
                value={formData.companyName}
                onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                className={listStyles.formInput}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                {t('governance.policies.template.owner', 'Owner')}
              </label>
              <input
                type="text"
                value={formData.owner}
                onChange={(e) => setFormData({ ...formData, owner: e.target.value })}
                className={listStyles.formInput}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                {t('governance.policies.template.department', 'Department')}
              </label>
              <input
                type="text"
                value={formData.department}
                onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                className={listStyles.formInput}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                {t('governance.policies.template.effectiveFrom', 'Effective From')}
              </label>
              <input
                type="date"
                value={formData.effectiveFrom}
                onChange={(e) => setFormData({ ...formData, effectiveFrom: e.target.value })}
                className={listStyles.formInput}
              />
            </div>
          </div>

        </form>
    </GovernanceInlineFormPanel>
  )
}

export default CreateFromTemplateModal


